interface EnumerateKeysOptions {
  obj: Record<string, unknown>;
  prefix?: string;
  includeArrayIndices?: boolean;
  includeNegativeIndices?: boolean;
  includeWildcard?: boolean;
}

/**
 * Traverses an object and returns a list of all key paths
 * @param options Configuration object for enumeration
 * @returns Array of key paths as strings
 */
export function enumerateKeys({
  obj,
  prefix = '',
  includeArrayIndices = true,
  includeNegativeIndices = true,
  includeWildcard = true,
}: EnumerateKeysOptions): string[] {
  const pathSet = new Set<string>();
  if (prefix) {
    pathSet.add(prefix);
  } else {
    pathSet.add('');
  }

  function traverse(current: unknown, path: string) {
    if (current && typeof current === 'object' && !Array.isArray(current)) {
      // Handle nested objects
      Object.entries(current).forEach(([key, value]) => {
        // Skip excluded keys when prefix is 'output'
        if (prefix === 'output' && outputExcludedKeys.includes(key)) {
          return;
        }
        const newPath = path ? `${path}.${key}` : key;
        if (!pathSet.has(newPath)) pathSet.add(newPath);
        traverse(value, newPath);
      });
    } else if (Array.isArray(current)) {
      // Handle arrays
      if (includeArrayIndices) {
        current.forEach((item, index) => {
          const newPath = `${path}[${index}]`;
          if (!pathSet.has(newPath)) pathSet.add(newPath);
          traverse(item, newPath);
        });
      }

      if (includeNegativeIndices) {
        // Include negative indices (-1, -2, -3) that reference elements from the end
        for (let offset = 1; offset <= 3; offset++) {
          if (current.length >= offset) {
            const negativePath = `${path}[-${offset}]`;
            if (!pathSet.has(negativePath)) pathSet.add(negativePath);
            traverse(current[current.length - offset], negativePath);
          }
        }
      }

      if (includeWildcard) {
        // Include wildcard path for any index
        const wildcardPath = `${path}[*]`;
        if (!pathSet.has(wildcardPath)) pathSet.add(wildcardPath);
        // Traverse the wildcard path using the first element (if exists) to capture nested keys generically
        if (current.length > 0) {
          traverse(current[0], wildcardPath);
        }
      }
    }
  }

  traverse(obj, prefix);
  return Array.from(pathSet);
}

const outputExcludedKeys = ['response_metadata', 'usage_metadata', 'id'];
